import {
  PatternOptionsObject,
  PointOptionsObject,
  SeriesAreaOptions,
  SeriesBarOptions,
  SeriesLineOptions,
  SeriesPieDataLabelsOptionsObject,
  SeriesPieOptions,
} from 'highcharts/highcharts';
import { CellValue } from 'types/spreadsheet';
import { WidgetType } from 'types/widget.types';
import { shuffle } from 'utils/array';
import { getWhiteOrDarkAlphaColor } from 'utils/color';
import { isDevelopment } from 'utils/environment';
import { transpose } from 'utils/matrix';
import { isFiniteNumber, parseFiniteNumberWithNaN } from 'utils/number';
import { AllWidgetData } from 'widgets/Widget.types';

import { DELIMITERS_REGEX, LEGACY_NUMBER_SEPARATOR, MIN_TABLE_COLUMNS, MIN_TABLE_ROWS } from './ChartWidget.config';
import {
  ChartDataSeries,
  ChartTableColumnSeries,
  ChartWidgetData,
  ControlledHeader,
  HighChartsWidgetTypesValue,
  SpreadsheetRow,
} from './ChartWidget.types';
import { DEFAULT_SEPARATOR } from './DataTableChartCommon/ChartSettingsNumberFormat/NumberFormat.config';
import { Y_LEGEND_OFFSET } from './DataTableChartCommon/DataTableChartCommon.config';
import {
  BACKUP_PATTERN,
  CustomPointSeries,
  DataTableChartWidgetPattern,
  HighChartsWidgetTypes,
  HighchartsOptions,
  PATTERNS,
  PatternId,
} from './DataTableChartCommon/DataTableChartCommon.types';
import { HalfPieChartWidgetData } from './HalfPieChartWidget/HalfPieChartWidget.types';
import { PieChartWidgetData } from './PieChartWidget/PieChartWidget.types';

// A type map that converses the array of column object to the array of column data array.
type SeriesDataMatrixType<T extends ChartDataSeries> = {
  [index in keyof T]: T[index]['data'];
} & { length: T['length'] };

/**
 * A util function that is used to remove the empty rows from the seriesData
 *
 * @param seriesData
 * @returns A modified seriesData which has the same structure as the original one, and the valid row number
 */
export const validateSpreadsheetRows = (seriesData: ChartWidgetData['seriesData']) => {
  const columnMatrix = seriesData.map(({ data }) => data) as SeriesDataMatrixType<ChartWidgetData['seriesData']>;
  const rowMatrix = transpose(columnMatrix);

  // Remove empty rows and the invalid rows
  const validRowMatrix = rowMatrix.reduce((matrix, currentRow) => {
    if (isValidRow(currentRow)) {
      matrix.push(currentRow);
    }
    return matrix;
  }, [] as typeof rowMatrix);

  const validColumnMatrix = transpose(validRowMatrix) as SeriesDataMatrixType<ChartWidgetData['seriesData']>;

  const validSeriesData = seriesData.map((columnData, index) => {
    return { ...columnData, data: validColumnMatrix[index] ?? [] };
  }) as ChartWidgetData['seriesData'];

  return {
    rowNum: validSeriesData.length > 0 ? validSeriesData[0].data.length : 0,
    seriesData: validSeriesData,
  };
};

interface ValidateSpreadsheetColumnsOptions {
  // True if not checking the column header to determine if it is a empty column
  // Default: false
  // Ex: When true, { name: any, data: [] } would be considered as empty column
  // Ex: When false, { name: undefined | null | '', data: [] } would be considered as empty column
  disableColumnHeader?: boolean;

  // True if not using the first column as the row header
  // Default: false
  // Ex: For the series data, [{ name: firstColumn, data: [] }, { name: secondColumn, data: [] }],
  // when true, the firstColumn would be considered as data drawed on the chart
  // when false, the firstColumn would be considered as the row header, which may be the axis label for the chart
  disableRowHeader?: boolean;
}

/**
 * A util function that is used to remove the empty columns from the seriesData
 *
 * @param seriesData
 * @returns A modified seriesData which has the same structure as the original one, and the valid row number
 */
export const validateSpreadsheetColumns = (
  seriesData: ChartWidgetData['seriesData'],
  options?: ValidateSpreadsheetColumnsOptions,
): [ChartTableColumnSeries<string | number>, ...ChartTableColumnSeries<number>[]] => {
  const { disableColumnHeader = false, disableRowHeader = false } = options ?? {};

  // Destruct and remove the first column so we can still keep the type info
  const [rowHeaderColumn = { name: '', data: [] }, ...otherDataSeries] = seriesData;
  const columnDataSeries = (disableRowHeader ? seriesData : otherDataSeries) ?? [];

  // Remove empty columns
  const columnData = columnDataSeries.reduce((processedSeries, { name, data, ...otherColumnData }) => {
    const hasValidHeader = !disableColumnHeader && typeof name === 'string' && name !== '';
    if (!isEmptyData(data) || hasValidHeader) {
      const validatedData = data.map(validateDataCell) as number[];

      // A valid data array should have at least one number that is not NaN
      const isValidDataArray = validatedData.filter((value) => !Number.isNaN(value)).length > 0;
      if (isValidDataArray || hasValidHeader) {
        processedSeries.push({
          ...otherColumnData,
          name: name ?? '',
          data: validatedData,
        });
      }
    }

    return processedSeries;
  }, [] as ChartTableColumnSeries<number>[]);

  const NonEmptyColumnData = (columnData.length > 0 ? columnData : [{ name: '', data: [] }]) as [
    ChartTableColumnSeries<string | number>,
    ...ChartTableColumnSeries<number>[],
  ];

  return disableRowHeader ? NonEmptyColumnData : [rowHeaderColumn, ...columnData];
};

/**
 * Validate a data cell to a data type used by the highchart.
 * Please check the DELIMITERS_REGEX defined on the ChartWidget.config.ts for the supported delimiters
 *
 * @param {any} value The value to be test
 * @returns {number} a number, including NaN
 */
export const validateDataCell = (value: CellValue): number => {
  // We take numbers as the value data on the charts
  if (typeof value === 'number') {
    return value;
  }

  // For string, we convert to a number
  if (typeof value === 'string') {
    const { prefix, suffix } = getNumberPrefixAndSuffix(value);

    // remove the leading and the ending spaces
    const withoutSpace = value.trim();

    // empty string or invalid string should return NaN instead of 0
    if (withoutSpace.length === 0 || withoutSpace.length < prefix.length + suffix.length) {
      return NaN;
    }

    const numberWithDelimiter = withoutSpace.slice(prefix.length, withoutSpace.length - suffix.length);

    // Remove the delimiters from the tested string
    const numberString = numberWithDelimiter.replace(DELIMITERS_REGEX, '');

    const withNegativeStr = prefix.includes('-') ? `-${numberString}` : numberString;

    // Check if it is a percentage string number
    if (suffix === '%') {
      return numberString.length > 0 ? Number(withNegativeStr) / 100 : NaN;
    }

    return Number(withNegativeStr);
  }

  // For other cell type such as boolean, symbol, bigNumber, function, etc..
  return NaN;
};

/**
 * Try to get the prefix and suffix of a number string
 *
 * @param {string} value the string to be tested
 * @returns {object} { prefix: string, suffix: string }
 */
export const getNumberPrefixAndSuffix = (value: string) => {
  const stringArray = value.trim().split(/(\d+)/);

  if (stringArray.length <= 1) {
    return {
      prefix: '',
      suffix: '',
    };
  }

  return {
    prefix: stringArray[0].trim(),
    suffix: stringArray[stringArray.length - 1].trim(),
  };
};

/**
 * Some column name on the table data is used for other purpose such as the color
 *
 * color of the chart is changed on another tab on the side panel, so we do not want to display on the spreadsheet
 *
 * @param {object} data The spreadsheet data
 * @param {number} headerRowNumber The total number of rows as column header in the table.
 * @return {object} The modified spreadsheet data
 */
export const validateSpreadsheetData = (data: SpreadsheetRow[], headerRowNumber: number) => {
  const columnMatrix = data.map(({ name, data: column }) => (headerRowNumber === 0 ? column : [name ?? '', ...column]));
  return transpose(columnMatrix);
};

/**
 * Check if the array represent an empty data set to the highchart
 * Currently, we only accept number or non-empty string
 *
 * @param {any[]} data the array to be test
 * @returns false if the array contains useful data
 */
export const isEmptyData = (data: CellValue[]) => {
  if (!data || data.length === 0) {
    return true;
  }

  for (let index = 0; index < data.length; ++index) {
    const value = data[index];
    if (typeof value === 'number' || (typeof value === 'string' && value !== '')) {
      return false;
    }
  }

  return true;
};

/**
 * Check if a row is valid,
 * it should have a non-empty header or any accepted data by highchart
 *
 * @param {any[]} data The row that contains any data
 * @returns {boolean} true if the row is valid, otherwise returns false
 */
export const isValidRow = (data: CellValue[]) => {
  // A valid row contains at least 1 data
  if (data.length < 1) {
    return false;
  }

  const [header, ...otherValues] = data;

  // If the header exists in a row, we define that it is a non-empty valid row.
  if ((typeof header === 'string' && header !== '') || isFiniteNumber(validateDataCell(header))) {
    return true;
  }

  // Check if otherValues contains at least one valid value.
  return otherValues.some((value) => isFiniteNumber(validateDataCell(value)));
};

/**
 * A helper function for 'extendSeriesData' to extend the row number and fill with empty string
 * unit tests are also covered by the 'extendSeriesData'
 *
 * @param {object} seriesData
 * @param {number} minRows The minimum row number that the table should have
 *
 * @returns seriesData
 */
export const fillEmptyRowData = (
  seriesData: ChartWidgetData['seriesData'],
  minRows = MIN_TABLE_ROWS,
): ChartWidgetData['seriesData'] => {
  const rowNumDiff = minRows - seriesData[0].data.length;
  if (rowNumDiff > 0) {
    return seriesData.map(({ data, ...otherProps }) => ({
      ...otherProps,
      data: [...data].concat(new Array(rowNumDiff).fill('')),
    })) as ChartWidgetData['seriesData'];
  }

  return seriesData;
};

/**
 * A helper function for 'extendSeriesData' to extend the column number and fill with empty string
 * unit tests are also covered by the 'extendSeriesData'
 *
 * @param {object} seriesData
 * @param {number} minColumns The minimum column number that the table should have
 *
 * @returns seriesData
 */
export const fillEmptyColumnData = (
  seriesData: ChartWidgetData['seriesData'],
  minColumns = MIN_TABLE_COLUMNS,
): ChartWidgetData['seriesData'] => {
  const colNumDiff = minColumns - seriesData.length;

  if (colNumDiff > 0) {
    const rowLength = seriesData[0].data.length;
    return [...seriesData].concat(
      new Array(colNumDiff).fill({
        data: new Array(rowLength).fill(''),
      }),
    ) as ChartWidgetData['seriesData'];
  }

  return seriesData;
};

/**
 * expend the side panel table to have empty rows and columns
 *
 * @param {object} seriesData
 * @param {number} minRows The minimum row number that the table should have
 * @param {number} minColumns The minimum column number that the table should have
 *
 * @returns seriesData
 */
export const extendSeriesData = (
  seriesData: ChartWidgetData['seriesData'],
  minRows?: number,
  minColumns?: number,
): ChartWidgetData['seriesData'] => {
  return fillEmptyColumnData(fillEmptyRowData(seriesData, minRows), minColumns);
};

/**
 * Add separator to number
 * @param value a number string
 * @param separator
 * @returns formatted number string
 */
export const addSeparator = (value: string, separator?: string) => {
  const parsedValue = Number(value);
  if (Number.isNaN(parsedValue)) {
    return '';
  }

  if (typeof separator === 'string' && separator !== DEFAULT_SEPARATOR && separator !== LEGACY_NUMBER_SEPARATOR) {
    const splitValue = value.split('.');

    // If it is a negative number, remove '-' and append afterwards to correctly place separator
    if (value[0] === '-') {
      splitValue[0] = '-' + splitValue[0].slice(1).replace(/\B(?=(\d{3})+(?!\d))/g, separator);
    } else {
      splitValue[0] = splitValue[0].replace(/\B(?=(\d{3})+(?!\d))/g, separator);
    }

    return splitValue.join('.');
  }

  return value;
};

interface FormatDataLabelProps {
  dataLabel?: string | number | null;
  prefix?: string;
  suffix?: string;
  precision?: string;
  separator?: string;
}

/**
 * Return a formatted data label that adds prefix, suffix, precision and separator
 *
 * @param param
 * @param param.dataLabel the number string that need to be formatted
 * @param param.prefix the prefix string that need to be added to start of the number string
 * @param param.suffix the suffix string that need to be added to end of the number string
 * @param param.precision round the number string to the given precision
 * @param param.separator the separator string that is used to separate the whole-number part of the number string
 * @returns
 */
export const formatDataLabel = ({
  dataLabel,
  prefix = '',
  suffix = '',
  precision,
  separator,
}: FormatDataLabelProps) => {
  const precisionNum = parseFiniteNumberWithNaN(precision);
  const labelNum = parseFiniteNumberWithNaN(dataLabel);
  const withPrecision =
    isFiniteNumber(precisionNum) && isFiniteNumber(labelNum) ? labelNum.toFixed(precisionNum) : dataLabel?.toString();

  return `${prefix}${addSeparator(withPrecision ?? '', separator)}${suffix}`;
};

/**
 * Convert a string to a word count series data that can be used by the table
 * The string can be a word, a sendtence, a paragraph, or a article
 *
 * example:
 *  data = 'hello, hello world'
 *  return = extendSeriesData([
 *   {
 *      name: 'Word',
 *      data: ['hello', 'world']
 *    },
 *   {
 *      name: 'Count',
 *      data: [2, 1]
 *    },
 *  ])
 *
 * @param data
 * @returns
 */
export const convertToWordCount = (data: string) => {
  const wordCountMap = new Map<string, number>();

  // TODO: we may extends the word count to support other languages and special characters
  // This regex is used to remove all the special characters then split the string into words
  // example: 'hello, hello. world' => ['hello', 'hello', 'world']
  // @see test convertToWordCount in ChartWidget.helpers.spec.ts
  const lines = data.replace(/[():'?]+/g, ' ').split(/[,. \n\t]+/g);
  lines.forEach((line) => {
    if (wordCountMap.has(line)) {
      wordCountMap.set(line, wordCountMap.get(line)! + 1);
    } else if (line !== '') {
      wordCountMap.set(line, 1);
    }
  });

  const seriesData: ChartWidgetData['seriesData'] = [
    {
      name: 'Word',
      data: Array.from(wordCountMap.keys()),
    },
    {
      name: 'Count',
      data: Array.from(wordCountMap.values()),
    },
  ];

  return extendSeriesData(seriesData);
};

/**
 * A util function that is used to shuffle the rows of the table
 *
 * @param seriesData
 * @returns A modified seriesData which has the same structure as the original one, with the row order changed
 */
export const shuffleRows = (seriesData: ChartWidgetData['seriesData']) => {
  const columnMatrix = seriesData.map(({ data }) => data) as SeriesDataMatrixType<ChartWidgetData['seriesData']>;
  const rowMatrix = transpose(columnMatrix);

  const shuffledRowMatrix = shuffle(rowMatrix) as typeof rowMatrix;

  const updatedColumnMatrix = transpose(shuffledRowMatrix) as SeriesDataMatrixType<ChartWidgetData['seriesData']>;

  const updatedSeriesData = seriesData.map(({ name }, index) => {
    return { name, data: updatedColumnMatrix[index] ?? [] };
  }) as ChartWidgetData['seriesData'];

  return extendSeriesData(updatedSeriesData);
};

/**
 * Builds a new Pattern Options Object
 * @param patternData
 * @param color
 * @param chartType
 */
const getPatternOptionsObject = (
  patternData: DataTableChartWidgetPattern,
  color: string = '#FFF',
  chartType: string = '',
): PatternOptionsObject => {
  const secondColor = getWhiteOrDarkAlphaColor(color, chartType === 'area' ? 0.75 : patternData.opacity || 0.25);

  return {
    aspectRatio: 0,
    backgroundColor: patternData.invertColors ? color : secondColor,
    color: '',
    image: '',
    opacity: chartType === 'area' ? 0.5 : 1,
    patternTransform: patternData.transform || '',
    path: {
      d: patternData.path?.d,
      stroke: patternData.invertColors ? secondColor : color,
      strokeWidth: patternData.path?.strokeWidth,
    },
    width: patternData.width || 20,
    height: patternData.height || 20,
  };
};

/**
 * Applies patterns to series or series data items.
 * @param highChartsOptions
 * @param patternsList
 */
export const applyPatterns = (highChartsOptions: HighchartsOptions, patternsList: PatternId[]) => {
  if (typeof highChartsOptions.series === 'undefined') {
    return;
  }

  let patternIterativeIndex = 0;

  // The color property is at Series level when having multiple series
  if (highChartsOptions.series.length > 1) {
    for (let i = 0; i < highChartsOptions.series.length; i++) {
      const seriesItem = highChartsOptions.series[i] as
        | SeriesBarOptions
        | SeriesAreaOptions
        | SeriesPieOptions
        | SeriesLineOptions;

      if (typeof patternsList[patternIterativeIndex] === 'undefined') {
        patternIterativeIndex = 0;
      }
      const patternData = PATTERNS[patternsList[patternIterativeIndex]] || BACKUP_PATTERN;
      patternIterativeIndex++;

      const isAreaChart = highChartsOptions?.chart?.type === 'area';
      const isStackedAreaChart = isAreaChart && highChartsOptions?.plotOptions?.area?.stacking === 'normal';

      // No pattern fill is required, only change line style.
      if (highChartsOptions?.chart?.type === 'line' || (isAreaChart && !isStackedAreaChart)) {
        (seriesItem as SeriesLineOptions).dashStyle = patternData.dashStyle || 'Solid';
        continue;
      }

      if (typeof seriesItem.color === 'string') {
        const patternOptions = getPatternOptionsObject(patternData, seriesItem.color, highChartsOptions?.chart?.type);

        // This prevents applying the pattern to the lines and lines' marker symbols for stacked area charts.
        if (isStackedAreaChart) {
          (seriesItem as SeriesAreaOptions).fillColor = {
            pattern: patternOptions,
          };
          continue;
        }

        seriesItem.color = {
          pattern: patternOptions,
        };
      }
    }
  } else if (highChartsOptions.series.length === 1) {
    // The color property is set at series' data item level
    const seriesItem = highChartsOptions.series[0] as
      | SeriesBarOptions
      | SeriesAreaOptions
      | SeriesPieOptions
      | SeriesLineOptions;

    if (typeof seriesItem.data === 'undefined') {
      return;
    }

    for (let j = 0; j < seriesItem.data.length; j++) {
      if (typeof patternsList[patternIterativeIndex] === 'undefined') {
        patternIterativeIndex = 0;
      }

      const seriesDataItem = seriesItem.data[j] as PointOptionsObject;
      const patternData = PATTERNS[patternsList[patternIterativeIndex]] || BACKUP_PATTERN;
      patternIterativeIndex++;

      const isAreaChart = highChartsOptions?.chart?.type === 'area';
      const isStackedAreaChart = isAreaChart && highChartsOptions?.plotOptions?.area?.stacking === 'normal';

      // No pattern fill is required, only change line style.
      if (highChartsOptions?.chart?.type === 'line' || (isAreaChart && !isStackedAreaChart)) {
        (seriesItem as SeriesLineOptions).dashStyle = patternData.dashStyle || 'Solid';
        continue;
      }

      if (typeof seriesDataItem.color === 'string') {
        if (highChartsOptions?.chart?.type === 'pie') {
          seriesDataItem.dataLabels = seriesDataItem.dataLabels || {};
          (seriesDataItem.dataLabels as SeriesPieDataLabelsOptionsObject).connectorColor = seriesDataItem.color;
        }

        seriesDataItem.dashStyle = 'Solid';
        seriesDataItem.color = {
          pattern: getPatternOptionsObject(patternData, seriesDataItem.color, highChartsOptions?.chart?.type),
        };
      }
    }
  }
};

/**
 * check if a widget is a chart
 * @param widgetType
 * @returns true if the widget is a chart
 */
export const isChart = (widgetType: WidgetType): widgetType is HighChartsWidgetTypes => {
  const otherChartTypes = [WidgetType.Table, WidgetType.StatChart];
  if (otherChartTypes.includes(widgetType)) return true;
  return HighChartsWidgetTypesValue.includes(widgetType as unknown as HighChartsWidgetTypes);
};

/**
 * get the chart type name and the preset name of a chart used by the mixpanel
 *
 * @param widgetType
 * @param widgetData
 * @returns {object} { widgetTypeName: string, preset: string }
 */
export const getChartInfo = (widgetType: WidgetType, widgetData?: AllWidgetData) => {
  let widgetTypeName = Object.entries(WidgetType).find(([_, typeString]) => typeString === widgetType)?.[0] ?? '';
  let preset = widgetTypeName;

  if (!isChart(widgetType)) {
    if (isDevelopment) {
      console.error(`Widget type ${widgetType} is not a chart type.`);
    }
    return { widgetTypeName, preset };
  }

  // Default preset is the chart name without the the word 'chart'.
  // Ex: PieChart -> Pie
  preset = widgetTypeName.slice(0, -5);

  if (widgetType === WidgetType.PieChart && widgetData) {
    const isDonutChart = (widgetData as PieChartWidgetData).generalOptions.innerSize;
    return { widgetTypeName, preset: isDonutChart ? 'DonutPie' : preset };
  }

  if (widgetType === WidgetType.HalfPieChart && widgetData) {
    const isDonutChart = (widgetData as HalfPieChartWidgetData).generalOptions.innerSize;
    return { widgetTypeName: 'PieChart', preset: isDonutChart ? 'HalfDonutPie' : 'HalfPie' };
  }

  if (widgetType === WidgetType.HalfPieChart) {
    return { widgetTypeName: 'PieChart', preset: 'HalfPie' };
  }

  // The combo chart does not want to ignore the word 'chart' on the preset
  if (widgetType === WidgetType.ComboChart) {
    return { widgetTypeName, preset: widgetTypeName };
  }

  return { widgetTypeName, preset };
};

/**
 * Find the index of the last column containing the data that can be used by the highcharts
 *
 * @param {ChartWidgetData['seriesData']} seriesData is the series data of the charts
 * @returns {number} a number that is the index of the last valid, or -1 if not found any valid column
 */
export const findTheLastValidColumnIndex = (seriesData: ChartWidgetData['seriesData']) => {
  for (let idx = seriesData.length - 1; idx >= 0; --idx) {
    const { name, data } = seriesData[idx];
    if (!isEmptyData(data) || (typeof name === 'string' && name !== '')) {
      return idx;
    }
  }

  return -1;
};

/**
 * Addthe control data to the widget data.
 * Ex: The combo chart needs the control header to define the way to use each column (y1 or y2)
 *
 * @param {ChartWidgetData['seriesData']} seriesData the series data of the charts
 * @returns seriesData that have the controll header appended.
 */
export const appendControlData = (seriesData: ChartWidgetData['seriesData']): ChartWidgetData['seriesData'] => {
  const lastValidColumnIndex = findTheLastValidColumnIndex(seriesData);

  return seriesData.map((columnData, index) => {
    if (index === lastValidColumnIndex && !columnData.controlledHeader) {
      return {
        ...columnData,
        controlledHeader: ControlledHeader.ComboLine,
      };
    }

    if (0 < index && index < lastValidColumnIndex && !columnData.controlledHeader) {
      return {
        ...columnData,
        controlledHeader: ControlledHeader.ComboColumn,
      };
    }

    return columnData;
  });
};

// Use Highcharts' internal methods to select the elements and modify them
export function CustomLegendRender(this: Highcharts.Chart | undefined) {
  if (this?.legend?.allItems) {
    this.legend.allItems.forEach((item) => {
      const seriesItem = item as CustomPointSeries;
      const EXCLUDED_ITEM_TYPES = ['line'];
      if (!EXCLUDED_ITEM_TYPES.includes(seriesItem.type || '')) {
        seriesItem.legendItem?.symbol?.attr({
          y: Y_LEGEND_OFFSET,
        });
      }
    });
  }
}
